import { useState } from "react";
import Layout from "../components/Layout";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid, AreaChart, Area,
} from "recharts";
import {
  Brain, Cube, CurrencyDollar, Gauge, Warning, ChartBar, ListChecks, Calendar, ShieldWarning, Flask,
} from "@phosphor-icons/react";
import data from "../data/assetIntelligenceV2.json";
import { Kpi, Panel, TabBar, AutoTable, fmt$, fmtK, fmtNum, NAVY, BLUE, GROUP_COLORS } from "../components/AIV2";

const A = data.admin;

const TABS = [
  { id: "overview", label: "Overview", icon: ChartBar },
  { id: "groups", label: "By Asset Group", icon: Cube },
  { id: "types", label: "By Asset Type", icon: ListChecks },
  { id: "plan", label: "24-Month Plan", icon: Calendar },
  { id: "risk", label: "Deferred Risk", icon: ShieldWarning },
];

export default function AssetIntelligenceV2Admin() {
  const [tab, setTab] = useState("overview");
  const k = A.kpis;

  const groupChart = A.summary_by_group.map((g) => ({ name: g["Asset Group"], value: g["Visible Replacement Value"] }));
  const renewalChart = A.renewal_forecast.map((r) => ({ year: r["Renewal Year"], value: r["Visible Replacement Value"] }));
  const capitalChart = A.ten_year_capital.map((r) => ({ year: r["Year"], value: r["Forecast Budget"] }));

  return (
    <Layout wide>
      <div data-testid="aiv2-admin-page">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0A0A0A] text-white"><Brain size={22} weight="fill" /></div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]">Asset Intelligence — Sample V2</h1>
              <p className="text-sm text-neutral-500">Portfolio-wide capital renewal model · AroFlo Asset Intelligence V4</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#D97706]/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#D97706]" data-testid="aiv2-sample-badge">
            <Flask size={14} weight="fill" /> Sample · Isolated data
          </span>
        </div>

        {/* KPI row */}
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Kpi icon={Cube} label="Total Assets" value={fmtNum(k.visible_assets)} />
          <Kpi icon={CurrencyDollar} label="Replacement Value" value={fmt$(k.replacement_value)} />
          <Kpi icon={CurrencyDollar} label="Material (60%)" value={fmt$(k.material_60)} accent="text-[#0055FF]" />
          <Kpi icon={CurrencyDollar} label="Labour (40%)" value={fmt$(k.labour_40)} accent="text-[#7C3AED]" />
          <Kpi icon={Gauge} label="Avg Condition (1–5)" value={k.avg_condition} sub="1 = new · 5 = replace" />
        </div>

        {/* Priority cards */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {A.priority.map((p, i) => (
            <div key={p.metric} className="rounded-sm border border-neutral-200 bg-white p-4 shadow-sm" data-testid="aiv2-priority-card">
              <div className="mb-1 flex items-center gap-2">
                <Warning size={16} className={i === 0 ? "text-[#DC2626]" : i === 1 ? "text-[#EA580C]" : "text-[#D97706]"} weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{p.metric}</span>
              </div>
              <p className="font-heading text-2xl font-black tracking-tight text-[#0A0A0A]">{fmtNum(p.count)} <span className="text-base font-bold text-neutral-400">assets</span></p>
              <p className="text-sm font-semibold text-neutral-600">{fmt$(p.value)}</p>
            </div>
          ))}
        </div>

        <TabBar tabs={TABS} active={tab} onChange={setTab} testidPrefix="aiv2-admin-tab" />

        {tab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Panel title="Replacement Value by Asset Group" testid="aiv2-chart-groups">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={groupChart} layout="vertical" margin={{ left: 30 }}>
                    <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmt$(v)} />
                    <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                      {groupChart.map((e, i) => <Cell key={i} fill={GROUP_COLORS[i % GROUP_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Renewal Forecast by Year" subtitle="Estimated replacement value due per year" testid="aiv2-chart-renewal">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={renewalChart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmt$(v)} />
                    <Bar dataKey="value" fill={NAVY} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>
            <Panel title="10-Year Capital Forecast" subtitle="Indicative annual renewal budget" testid="aiv2-chart-capital">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={capitalChart}>
                  <defs>
                    <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={BLUE} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Area type="monotone" dataKey="value" stroke={BLUE} strokeWidth={2} fill="url(#capGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        )}

        {tab === "groups" && <Panel title="Summary by Asset Group" testid="aiv2-groups-table"><AutoTable rows={A.summary_by_group} maxHeight="34rem" /></Panel>}
        {tab === "types" && <Panel title="Summary by Asset Type" testid="aiv2-types-table"><AutoTable rows={A.summary_by_type} maxHeight="34rem" /></Panel>}
        {tab === "plan" && <Panel title="24-Month Replacement Plan" subtitle={`${A.replacement_plan.length} assets identified for renewal assessment`} testid="aiv2-plan-table"><AutoTable rows={A.replacement_plan} maxHeight="38rem" /></Panel>}
        {tab === "risk" && <Panel title="Deferred Risk Register" subtitle={`${A.deferred_risk.length} high risk / poor condition assets`} testid="aiv2-risk-table"><AutoTable rows={A.deferred_risk} maxHeight="38rem" /></Panel>}

        <p className="mt-6 text-xs text-neutral-400" data-testid="aiv2-disclaimer">
          Sample dataset only — isolated from live AroFlo / site data. Replacement costs are planning allowances (60% material / 40% labour) and do not replace site-specific quotations or engineering review.
        </p>
      </div>
    </Layout>
  );
}
